'use client';

/**
 * ProjectPerformance - Project-level performance metrics
 *
 * Shows performance data for a selected project:
 * - RFQ and PO counts
 * - Vendor response activity and averages
 *
 * @component_role Project-level Insights
 * @business_rule Aggregates project metrics across RFQs, POs, and vendor responses.
 *   Based on Neo4j relationship data, not AI synthesis.
 * @user_outcome Project report card - see procurement activity at a glance.
 *   Identify active vs. stalled projects.
 * @integration_point BFF data gateway → project_perf strategy
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { Loader2, XCircle, FolderKanban, BarChart3 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SectionHeader } from '../details/shared';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface ProjectPerformanceProps {
  projectId: string;
}

export function ProjectPerformance({ projectId }: ProjectPerformanceProps) {
  const perfStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'perf'));
  const { data, isLoading, error } = useStrategyData(perfStrategy ?? 'project_perf', { semantic_id: projectId });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <Loader2 className="h-8 w-8 mb-2 animate-spin" />
        <p className="text-sm">Loading project data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-destructive">
        <XCircle className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Failed to load project data</p>
        <p className="text-xs mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  const perf = data?.data as Record<string, unknown> | undefined;

  if (!perf || !perf.name) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <FolderKanban className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Project not found</p>
      </div>
    );
  }

  const rfqCount = (perf.rfq_count as number) ?? 0;
  const poCount = (perf.po_count as number) ?? 0;
  const responseCount = (perf.response_count as number) ?? 0;
  const vendorCount = (perf.vendor_count as number) ?? 0;
  const avgAmount = perf.avg_response_amount as number | null;

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Performance</span>
        </div>
        <Badge variant="secondary" className="text-[10px] px-1.5">{String(perf.name)}</Badge>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto scrollbar-thin">
        <div className="space-y-4 p-4">
          {/* Overview */}
          <div>
            <SectionHeader title="OVERVIEW" />
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 rounded-lg bg-card border border-border text-center">
                <div className="text-sm font-semibold">{rfqCount}</div>
                <div className="text-[10px] text-muted-foreground">RFQs</div>
              </div>
              <div className="p-2 rounded-lg bg-card border border-border text-center">
                <div className="text-sm font-semibold">{poCount}</div>
                <div className="text-[10px] text-muted-foreground">Purchase Orders</div>
              </div>
            </div>
          </div>

          {/* Activity */}
          <div>
            <SectionHeader title="ACTIVITY" />
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg bg-card border border-border text-center">
                <div className="text-sm font-semibold">{responseCount}</div>
                <div className="text-[10px] text-muted-foreground">Responses</div>
              </div>
              <div className="p-2 rounded-lg bg-card border border-border text-center">
                <div className="text-sm font-semibold">{vendorCount}</div>
                <div className="text-[10px] text-muted-foreground">Vendors</div>
              </div>
              <div className="p-2 rounded-lg bg-card border border-border text-center">
                <div className="text-sm font-semibold">
                  {avgAmount != null
                    ? `R ${avgAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                    : '-'}
                </div>
                <div className="text-[10px] text-muted-foreground">Avg Amount</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-[10px] text-muted-foreground/60 text-center pt-1 pb-2">
        Neo4j knowledge graph
      </div>
    </div>
  );
}
